import React, { useState, useEffect, useMemo } from 'react';
import { createGraphiQLFetcher } from '@graphiql/toolkit';
import { GraphiQL } from 'graphiql';
import { Typography, Box, CircularProgress } from '@mui/material';
import { fetchAuthSession } from 'aws-amplify/auth';
import 'graphiql/graphiql.css';

import settings from '../../awsExports';
import { createAppsyncWsClient } from './appsyncWsClient';

const GraphqlPlayground = () => {
  const [authHeader, setAuthHeader] = useState('');

  useEffect(() => {
    const loadSession = async () => {
      try {
        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken;
        setAuthHeader(idToken ?? null);
      } catch (err) {
        console.error('Failed to fetch auth session:', err);
        setAuthHeader(null);
      }
    };

    loadSession();
  }, []);

  const fetcher = useMemo(() => {
    if (!authHeader) return null;
    const httpsEndpoint = settings.aws_appsync_graphqlEndpoint;
    // The graphiql-toolkit calls wsClient.subscribe / dispose for any
    // `subscription` operation. AppSync's realtime endpoint needs a fresh
    // Cognito ID token in the auth payload, so we refetch on every connect
    // rather than capturing the one resolved at mount time.
    const wsClient = createAppsyncWsClient({
      httpsEndpoint,
      getAuthToken: async () => {
        const session = await fetchAuthSession();
        return session.tokens?.idToken?.toString();
      },
    });
    return createGraphiQLFetcher({
      url: httpsEndpoint,
      headers: { Authorization: authHeader },
      wsClient,
    });
  }, [authHeader]);

  if (!fetcher) {
    return <CircularProgress />;
  }

  return <GraphiQL fetcher={fetcher} />;
};

const ApiPlayground = () => {
  return (
    <Box
      padding='1.5rem 2.5rem 0rem 2.5rem'
      sx={{
        backgroundColor: 'background.main',
        minHeight: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
      }}
    >
      <Typography variant="h1" color="emphasis.dark">
        Mesh API Playground
      </Typography>
      <GraphqlPlayground />
    </Box>
  );
};

export default ApiPlayground;
